import { Text, TextInput, View, ScrollView, Pressable, Platform, useWindowDimensions, TouchableOpacity, FlatList } from 'react-native';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Animatable from 'react-native-animatable';
import { Entypo, FontAwesome5, MaterialIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import Checkbox from 'expo-checkbox';
import { useDispatch, useSelector } from 'react-redux';
import useGlobalUser from '@hooks/getGlobalUser';
import { DefaultRootState } from '@reduxLocal/persistState';
import { ADD_STICKY_NOTE, CHANGE_STICKY_NOTE, MARK_NOTE_COMPLETE, NEXT_STICKY_NOTE, PIN_NOTE, PREVIOUS_STICKY_NOTE, REMOVE_STICKY_NOTE, SHOW_STICKY_NOTES_MODAL } from '@reduxLocal/actions/types';
import apis, { addStickyNote } from '@api/api';
import { NoteTypes, IndividualNote } from './NoteTypes';
import { useTranslation } from 'react-i18next';
import decreaseBrightness from '@hooks/decreaseBrightness';
import TempScreen from '@components/Global/htmlEditor/HtmlEditor';
import { Popover } from '@mui/material';
import { Card, XStack, YStack } from 'tamagui';
import { getModernCardStyle, getModernActionStyle, getModernTextColor } from '@stylesheet';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useSelector as useReduxSelector } from 'react-redux';


const colors = ["#ffc09f", "#ffee93", "#fcf5c7", "#a0ced9", "#adf7b6"];

const RenderNote = ({ unassigned = false, unbounded, note, count, refetch, isStuck = false, inputCallback = () => { } }: NoteTypes) => {
  // Get theme for consistent styling
  const theme = themeSelector();
  
  // Defensive null checks for note object
  const safeNote = note || { _id: "", content: "", completed: false, color: "#ffc09f" };
  
  // Debug logging
  console.log('RenderNote Debug:', { note, safeNote, count });
  
  const userId = useGlobalUser();
  const [checked, setChecked] = useState(safeNote.completed || false);
  const [currentStickyNote, setCurrentStickyNote] = useState<IndividualNote>({ 
    _id: safeNote._id || "", 
    content: safeNote.content || "", 
    completed: safeNote.completed || false, 
    color: safeNote.color || "#ffc09f" 
  });
  const { t } = useTranslation();
  const [showColorSelect, setShowColorSelect] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const stickyNoteRef = useRef(null);
  const dispatch = useDispatch();
  const [colorSelect, setColorSelect] = useState(null);

  const ColorSelect = ({color, selectedNote, unassigned}) => {

    const _handleFunction = async () => {
      if (unassigned) {
        _setNoteColor(color);
        setShowColorSelect(false);
      } else {
      await apis.setNoteColor(userId, safeNote).then(res => {
        _setNoteColor(color);
        setShowColorSelect(false);
      })
      }
    };

    return (
      <TouchableOpacity 
        style={{ 
          width: 32,
          height: 32,
          borderRadius: 16,
          backgroundColor: color, 
          borderWidth: 3, 
          borderColor: 'white',
          shadowOpacity: 0.3,
          shadowRadius: 4,
          shadowOffset: { width: 0, height: 2 },
          elevation: 3,
        }} 
        onPress={() => _handleFunction()} 
      />
    )
  }


  const mouseDownHandler = (event) => {
    setPosition({
      x: event.clientX,
      y: event.clientY
    })
  }

  function _setNoteColor(color) {
    setCurrentStickyNote({ ...currentStickyNote, color: color })
  }

  async function _handleCheck() {
    if (!checked) {
      try {
        await apis.markNoteComplete(userId, currentStickyNote);
        const updatedNote = { ...currentStickyNote, completed: true };
        setCurrentStickyNote(updatedNote);
        dispatch({ type: MARK_NOTE_COMPLETE, payload: updatedNote });
        setChecked(true);
        refetch();
      } catch (error) {
        console.error('Error marking note complete:', error);
      }
    }
  }

  async function _handleDeleteNote() {
    try {
      await apis.removeStickyNote(userId, safeNote._id);
      dispatch({ type: REMOVE_STICKY_NOTE, payload: safeNote._id });
      refetch();
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  }

  async function _handleModifyNote(value) {
    console.log('value', value);
    if(unassigned){
      return setCurrentStickyNote({ ...currentStickyNote, content: value });
    }
    if (currentStickyNote?.content === safeNote.content && currentStickyNote.color === safeNote.color) {
      return
    }
    if(inputCallback){
      inputCallback()
    } 
    else {
      try {
        const response = await apis.modifyStickyNote(currentStickyNote);
        if (response?.data?.success) {
          dispatch({ type: CHANGE_STICKY_NOTE, payload: currentStickyNote });
        }
      } catch (error) {
        console.error('Error modifying note:', error);
      }
    }
  }

  async function _handleAddNote() {
    await apis.addStickyNote(currentStickyNote).then(res => {
      if (res?.data?.success) {
        console.log('added note', res.data);
        dispatch({ type: ADD_STICKY_NOTE, payload: currentStickyNote })
      }
    })
  }

  return (
    <Card 
      style={{
        ...getModernCardStyle(theme, 'medium'),
        backgroundColor: currentStickyNote.color || 'yellow',
        borderWidth: 2,
        borderColor: 'rgba(255,255,255,0.3)',
        marginVertical: 6,
        marginHorizontal: Platform.OS === 'web' ? 8 : 0,
        minHeight: unbounded ? undefined : (Platform.OS === 'web' ? 200 : 180),
        maxHeight: Platform.OS === 'web' ? (unbounded ? undefined : 400) : undefined,
        minWidth: unbounded ? "90%" : (Platform.OS === 'web' ? 300 : '100%'),
        maxWidth: unbounded ? undefined : (Platform.OS === 'web' ? 350 : '100%'),
        width: Platform.OS === 'web' ? undefined : '100%',
      }}
    >
      <XStack padding="$4" flex={1}>
        <YStack flex={1} paddingRight="$3">
          <ScrollView scrollEnabled={Platform.OS !== 'web'} style={{ flex: 1 }}>
            <TempScreen 
              initialContentHTML={safeNote.content} 
              onFocus={() => console.log('focused')} 
              noBackground 
              ref={stickyNoteRef} 
              onChange={_handleModifyNote} 
              placeHolder="Add a sticky note" 
            />
          </ScrollView>
        </YStack>
        
        <YStack space="$3" alignItems="center" minWidth={40}>
          {/* Color selector button */}
          <TouchableOpacity 
            style={{
              width: 32,
              height: 32,
              borderRadius: 16,
              backgroundColor: currentStickyNote.color,
              borderWidth: 2,
              borderColor: 'white',
              justifyContent: 'center',
              alignItems: 'center',
              shadowOpacity: 0.2,
              shadowRadius: 3,
              shadowOffset: { width: 0, height: 1 },
              elevation: 2,
            }}
            onPress={(ev) => { setColorSelect(ev.currentTarget) }} 
          />
          
          {/* Pin button - only show on web and when not stuck */}
          {Platform.OS === 'web' && !isStuck && (
            <TouchableOpacity
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                backgroundColor: '#3b82f6',
                justifyContent: 'center',
                alignItems: 'center',
                shadowOpacity: 0.2,
                shadowRadius: 3,
                shadowOffset: { width: 0, height: 1 },
                elevation: 2,
              }}
              onPress={async () => {
                if (unassigned) {
                  _handleAddNote().then(() => {
                    dispatch({ type: PIN_NOTE, payload: currentStickyNote })
                  })
                } else {
                  dispatch({ type: PIN_NOTE, payload: currentStickyNote });
                }
              }}
            >
              <MaterialIcons name="push-pin" size={16} color="white" />
            </TouchableOpacity>
          )}
          
          {!unassigned ? (
            <>
              {/* Delete button */}
              <TouchableOpacity 
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  backgroundColor: '#ef4444',
                  justifyContent: 'center',
                  alignItems: 'center',
                  shadowOpacity: 0.2,
                  shadowRadius: 3,
                  shadowOffset: { width: 0, height: 1 },
                  elevation: 2,
                }}
                onPress={() => _handleDeleteNote()}
              >
                <MaterialIcons name="delete-outline" size={16} color="white" />
              </TouchableOpacity>
              
              {/* Checkbox */}
              <View style={{
                padding: 4,
                borderRadius: 8,
                backgroundColor: 'rgba(255,255,255,0.3)',
              }}>
                <Checkbox 
                  style={{ 
                    width: 20, 
                    height: 20,
                  }} 
                  value={checked} 
                  onChange={() => _handleCheck()} 
                />
              </View>
            </>
          ) : (
            /* Save button for unassigned notes */
            <TouchableOpacity 
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                backgroundColor: '#10b981',
                justifyContent: 'center',
                alignItems: 'center',
                shadowOpacity: 0.2,
                shadowRadius: 3,
                shadowOffset: { width: 0, height: 1 },
                elevation: 2,
              }}
              onPress={() => _handleAddNote()}
            >
              <MaterialIcons name="save" size={16} color="white" />
            </TouchableOpacity>
          )}
        </YStack>
      </XStack>
      
      {/* Bottom gradient */}
      <LinearGradient
        colors={['transparent', decreaseBrightness(currentStickyNote.color || '#FFFF00')]}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 12,
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
          opacity: Platform.OS !== 'web' ? 0.3 : 0.4,
        }}
      />
      
      {/* Color selection popover */}
      <Popover
        open={!!colorSelect}
        anchorEl={colorSelect}
        onClose={() => setColorSelect(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{ borderRadius: 20 }}
        style={{ padding: 10 }}
      >
        <View style={{ padding: 16 }}>
          <Text style={{
            fontSize: 14,
            fontWeight: '600',
            color: getModernTextColor(theme),
            marginBottom: 12,
            textAlign: 'center',
          }}>
            {t('Choose Color') || 'Choose Color'}
          </Text>
          <FlatList 
            data={colors} 
            renderItem={({ item }) => (
              <ColorSelect unassigned={unassigned} color={item} selectedNote={undefined} />
            )} 
            horizontal 
            scrollEnabled={false} 
            contentContainerStyle={{ 
              gap: 12, 
              flexDirection: "row",
              justifyContent: 'center',
            }} 
          />
        </View>
      </Popover>
    </Card>
  )
}
export default RenderNote;